import { AuthClient } from '@/api/auth-client'
import { Company } from '@/api/model/company'
import { isArray } from 'lodash'
import moment from 'moment'

export interface ProxyRequest {
    path: string
    method: string
    query?: any
    options?: any
}

export class WinckOffice {
    constructor(
        public id: string,
        public name: string,
        public emailContact: string = '',
        public emailAppointment: string = '',
        public phonenumber: string = '',
        public realworksServiceSettings?: WinckRealworksServiceSettings,
    ) {}

    get hasWonenToken(): boolean {
        return !!(this.realworksServiceSettings && this.realworksServiceSettings.wonenToken !== null)
    }

    get wonenToken(): string|null {
        if (!this.hasWonenToken) {
            return null
        }

        return this.realworksServiceSettings.wonenToken
    }

    static fromData(data: any): WinckOffice {
        if (data === null || typeof data === 'string' || data instanceof WinckOffice) {
            return data
        }

        return new WinckOffice(
            data.id,
            data.name,
            data.emailContact,
            data.emailAppointment,
            data.phonenumber,
            data.realworksServiceSettings,
        )
    }
}

export interface WinckRealworksServiceSettings {
    relatiesToken?: string
    wonenToken?: string
}

export class WinckCollection {
    constructor(
        public id: string,
        public name: string,
        public filters: WinckFilter[],
    ) {}

    static fromData(data: any): WinckCollection {
        if (data === null || typeof data === 'string' || data instanceof WinckCollection) {
            return data
        }

        return new WinckCollection(
            data.id,
            data.name,
            Object.values(data.filters || {}).map(entry => WinckFilter.fromData(entry)),
        )
    }
}

export class WinckFilter {
    constructor(
        public field: string,
        public fieldValues: object|string[],
    ) {}

    static fromData(data: any): WinckFilter {
        if (data === null || typeof data === 'string' || data instanceof WinckFilter) {
            return data
        }

        return new WinckFilter(
            data.field,
            data.fieldValues,
        )
    }
}

export class WinckFilterConfig {
    constructor(
        public name: string,
        public type: WinckFilterFieldType,
        public values: string[],
    ) {}

    static fromData(data: any): WinckFilterConfig {
        if (data === null || typeof data === 'string' || data instanceof WinckFilterConfig) {
            return data
        }

        return new WinckFilterConfig(
            data.name,
            data.type,
            data.values ?? [],
        )
    }
}

export type WinckFilterFieldType =
    | 'range'
    | 'value'

export class WinckScheduledCollection {
    constructor(
        public id: string,
        public collection: WinckCollection,
        public date?: string,
        public dayOfWeek?: number,
        public fromTime?: string,
        public untilTime?: string,
        public screens: WinckScreen[] = [],
    ) {}

    static fromData(data: any): WinckScheduledCollection {
        if (data === null || typeof data === 'string' || data instanceof WinckScheduledCollection) {
            return data
        }

        return new WinckScheduledCollection(
            data.id,
            data.collection ? WinckCollection.fromData(data.collection) : null,
            data.date,
            data.dayOfWeek,
            data.fromTime ? moment(data.fromTime).utc().format('HH:mm') : null,
            data.untilTime ? moment(data.untilTime).utc().format('HH:mm') : null,
            Object.values(data.screens || {}).map(entry => WinckScreen.fromData(entry)),
        )
    }
}

export class WinckScreen {
    constructor(
        public id: string,
        public name: string,
        public samsungId: string,
        public officeId: string,
    ) {}

    static fromData(data: any): WinckScreen {
        if (data === null || typeof data === 'string' || data instanceof WinckScreen) {
            return data
        }

        return new WinckScreen(
            data.id,
            data.name,
            data.samsungId,
            data.officeId,
        )
    }
}

export class WinckProxyClient extends AuthClient {
    async getOffices(company: Company): Promise<WinckOffice[]> {
        const offices = await this.request<WinckOffice>({
            method: 'GET',
            path: `/api/company/companies/${company.winckCompanyId}/offices`,
            query: {
                pagination: false,
            },
        }, WinckOffice.fromData)

        return offices.filter(office => office.hasWonenToken === false)
    }

    async createOffice(company: Company, office: WinckOffice): Promise<void> {
        await this.request({
            method: 'POST',
            path: `/api/company/offices`,
            options: {
                json: {
                    companyId: company.winckCompanyId,
                    ...office,
                },
            },
        })
    }

    async getOffice(id: string): Promise<WinckOffice> {
        return await this.request<WinckOffice>({
            method: 'GET',
            path: `/api/company/offices/${id}`,
        }, WinckOffice.fromData)
    }

    async deleteOffice(id: string): Promise<void> {
        await this.request({
            method: 'DELETE',
            path: `/api/company/offices/${id}`,
        })
    }

    async updateOfficeName(id: string, name: string): Promise<void> {
        await this.request({
            method: 'PUT',
            path: `/api/company/offices/${id}/change_name`,
            options: {
                json: {
                    name,
                },
            },
        })
    }

    async updateOfficeEmailContact(id: string, emailContact: string): Promise<void> {
        await this.request({
            method: 'PUT',
            path: `/api/company/offices/${id}/change_email_contact`,
            options: {
                json: {
                    emailContact,
                },
            },
        })
    }

    async updateOfficeEmailAppointment(id: string, emailAppointment: string): Promise<void> {
        await this.request({
            method: 'PUT',
            path: `/api/company/offices/${id}/change_email_appointment`,
            options: {
                json: {
                    emailAppointment,
                },
            },
        })
    }

    async updateOfficePhonenumber(id: string, phonenumber: string): Promise<void> {
        await this.request({
            method: 'PUT',
            path: `/api/company/offices/${id}/change_phonenumber`,
            options: {
                json: {
                    phonenumber,
                },
            },
        })
    }

    async getCollections(company: Company): Promise<WinckCollection[]> {
        return this.request<WinckCollection>({
            method: 'GET',
            path: `/api/service/sam/collections`,
            query: {
                company: company.winckCompanyId,
                pagination: false,
            },
        }, WinckCollection.fromData)
    }

    async createCollection(company: Company, collection: WinckCollection): Promise<void> {
        await this.request({
            method: 'POST',
            path: `/api/service/sam/collections`,
            options: {
                json: {
                    companyId: company.winckCompanyId,
                    ...collection,
                },
            },
        })
    }

    async getCollection(id: string): Promise<WinckCollection> {
        return await this.request<WinckCollection>({
            method: 'GET',
            path: `/api/service/sam/collections/${id}`,
        }, WinckCollection.fromData)
    }

    async deleteCollection(id: string): Promise<void> {
        await this.request({
            method: 'DELETE',
            path: `/api/service/sam/collections/${id}`,
        })
    }

    async updateCollection(collection: WinckCollection): Promise<void> {
        await this.request({
            method: 'PUT',
            path: `/api/service/sam/collections/${collection.id}`,
            options: {
                json: collection,
            },
        })
    }

    async getScheduledCollections(company: Company): Promise<WinckScheduledCollection[]> {
        return this.request<WinckScheduledCollection>({
            method: 'GET',
            path: `/api/service/sam/scheduled_collections`,
            query: {
                company: company.winckCompanyId,
                pagination: false,
            },
        }, WinckScheduledCollection.fromData)
    }

    async createScheduledCollection(company: Company, scheduledCollection: WinckScheduledCollection): Promise<void> {
        await this.request({
            method: 'POST',
            path: `/api/service/sam/scheduled_collections`,
            options: {
                json: {
                    companyId: company.winckCompanyId,
                    collectionId: scheduledCollection.collection.id,
                    id: scheduledCollection.id,
                    date: scheduledCollection.date,
                    dayOfWeek: scheduledCollection.dayOfWeek,
                    fromTime: scheduledCollection.fromTime,
                    untilTime: scheduledCollection.untilTime,
                    screens: scheduledCollection.screens.map(screen => screen.id),
                },
            },
        })
    }

    async getScheduledCollection(id: string): Promise<WinckScheduledCollection> {
        return await this.request<WinckScheduledCollection>({
            method: 'GET',
            path: `/api/service/sam/scheduled_collections/${id}`,
        }, WinckScheduledCollection.fromData)
    }

    async deleteScheduledCollection(id: string): Promise<void> {
        await this.request({
            method: 'DELETE',
            path: `/api/service/sam/scheduled_collections/${id}`,
        })
    }

    async updateScheduledCollection(scheduledCollection: WinckScheduledCollection): Promise<void> {
        await this.request({
            method: 'PUT',
            path: `/api/service/sam/scheduled_collections/${scheduledCollection.id}`,
            options: {
                json: {
                    collectionId: scheduledCollection.collection.id,
                    id: scheduledCollection.id,
                    date: scheduledCollection.date,
                    dayOfWeek: scheduledCollection.dayOfWeek,
                    fromTime: scheduledCollection.fromTime,
                    untilTime: scheduledCollection.untilTime,
                    screens: scheduledCollection.screens.map(screen => screen.id),
                },
            },
        })
    }

    async getScreens(officeId: string): Promise<WinckScreen[]> {
        const screens = await this.request<WinckScreen>({
            method: 'GET',
            path: `/api/service/sam/screens`,
            query: {
                office: officeId,
                pagination: false,
            },
        }, WinckScreen.fromData)

        return screens.map(screen => {
            return {
                ...screen,
                officeId,
            }
        })
    }

    async createScreen(screen: WinckScreen): Promise<void> {
        await this.request({
            method: 'POST',
            path: `/api/service/sam/screens`,
            options: {
                json: screen,
            },
        })
    }

    async getScreen(id: string): Promise<WinckScreen> {
        return await this.request<WinckScreen>({
            method: 'GET',
            path: `/api/service/sam/screens/${id}`,
        }, WinckScreen.fromData)
    }

    async deleteScreen(id: string): Promise<void> {
        await this.request({
            method: 'DELETE',
            path: `/api/service/sam/screens/${id}`,
        })
    }

    async updateScreen(screen: WinckScreen): Promise<void> {
        await this.request({
            method: 'PUT',
            path: `/api/service/sam/screens/${screen.id}`,
            options: {
                json: screen,
            },
        })
    }

    async getFilterConfigs(company: Company): Promise<WinckFilterConfig[]> {
        return this.request<WinckFilterConfig>({
            method: 'GET',
            path: `/api/service/sam/fields`,
            query: {
                pagination: false,
                company: company.winckCompanyId,
            },
        }, WinckFilterConfig.fromData)
    }

    async request<T>(request: ProxyRequest, parser: (data: any) => T = null): Promise<any> {
        try {
            const data = (await this.client.post(`${process.env.VUE_APP_KEYVUE_BACKEND_BASEURL}/proxy`, request)).data

            if (isArray(data)) {
                return parser ? data.map(entry => parser(entry)) : data
            }

            return parser ? parser(data) : data

        } catch (error) {
            console.error('Proxy error:', error.message)
        }
    }
}
